import { useEffect, useState } from "react"

import { cn } from "@/lib/utils"

// Muted monochrome avatar — gray surface + initials, per DECISIONS §Color
// ("avatars → muted monochrome", which overrides the prototype's per-person
// colored chips). `me` renders the dark "you" chip so the current user is
// distinguishable from the team.
export function Avatar({ initials, name, src, size = 28, me = false, className, style }) {
  const [imageFailed, setImageFailed] = useState(false)

  useEffect(() => {
    setImageFailed(false)
  }, [src])

  return (
    <span
      title={name}
      aria-label={src && !imageFailed ? undefined : name}
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-bold",
        me ? "bg-foreground text-background" : "bg-secondary text-muted-foreground",
        className
      )}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.42), ...style }}
    >
      {src && !imageFailed ? (
        <img
          src={src}
          alt={name ? `${name} profile picture` : "Profile picture"}
          className="size-full object-cover"
          onError={() => setImageFailed(true)}
        />
      ) : (
        initials
      )}
    </span>
  )
}

// Overlapping stack with a +N overflow chip. Each avatar rings the card surface
// so the overlaps stay legible (prototype `.who2 .avatar` uses a 2px border).
export function AvatarStack({ people = [], max = 3, size = 28 }) {
  if (people.length === 0) {
    return <span className="text-[11.5px] text-ink-3">—</span>
  }

  const shown = people.slice(0, max)
  const overflow = people.length - shown.length

  return (
    <div className="flex items-center">
      {shown.map((person, i) => (
        <Avatar
          key={`${person.name}-${i}`}
          initials={person.initials}
          name={person.name}
          size={size}
          className="ring-2 ring-card"
          style={i > 0 ? { marginLeft: -7 } : undefined}
        />
      ))}

      {overflow > 0 && (
        <span
          className="inline-flex shrink-0 items-center justify-center rounded-full bg-secondary font-bold text-muted-foreground ring-2 ring-card"
          style={{ width: size, height: size, fontSize: Math.round(size * 0.38), marginLeft: -7 }}
        >
          +{overflow}
        </span>
      )}
    </div>
  )
}
